import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PeoplePicker } from '@/components/PeoplePicker';
import { Avatar, AvatarStack, EmptyState, ErrorNotice, Spinner } from '@/components/ui';
import {
  useAddProjectMember,
  useProject,
  useProjects,
  useRemoveProjectMember,
} from '@/hooks/queries';
import { useProjectStore } from '@/stores/project';
import type { ProjectMember, ProjectRole } from '@/types/api';
import { useState } from 'react';

const ROLE_LABEL: Record<ProjectRole, string> = {
  PRODUCT_OWNER: 'Product Owner',
  SCRUM_MASTER: 'Scrum Master',
  DEVELOPER: 'Desarrollo',
  VIEWER: 'Observador',
};

const ROLE_HINT: Record<ProjectRole, string> = {
  PRODUCT_OWNER: 'Administra el proyecto y sus sprints',
  SCRUM_MASTER: 'Administra el proyecto y sus sprints',
  DEVELOPER: 'Crea y edita tareas',
  VIEWER: 'Solo lectura',
};

/**
 * Proyectos y su gente. Sumar a alguien aqui le abre de golpe el backlog, el
 * tablero y el canal de chat del proyecto: la pertenencia es lo unico que
 * decide que ve cada persona.
 */
export function ProjectsPage() {
  const { data: projects, isLoading, error } = useProjects();
  const { activeProjectId, setActiveProject } = useProjectStore();
  const [expandedId, setExpandedId] = useState<string | null>(activeProjectId);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorNotice>{(error as Error).message}</ErrorNotice>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="Todavia no hay proyectos"
          hint="Crea el primero desde el selector de la barra lateral."
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-6 py-5">
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          projectId={project.id}
          expanded={expandedId === project.id}
          onToggle={() => setExpandedId((id) => (id === project.id ? null : project.id))}
          onOpen={() => setActiveProject(project.id)}
          isActive={project.id === activeProjectId}
        />
      ))}
    </div>
  );
}

function ProjectCard({
  projectId,
  expanded,
  onToggle,
  onOpen,
  isActive,
}: {
  projectId: string;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  isActive: boolean;
}) {
  const { data: project } = useProject(projectId);
  const [adding, setAdding] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(null);

  const addMember = useAddProjectMember(projectId);
  const removeMember = useRemoveProjectMember(projectId);
  const [role, setRole] = useState<ProjectRole>('DEVELOPER');

  if (!project) {
    return (
      <section className="rounded-panel border border-line bg-surface p-4">
        <Spinner />
      </section>
    );
  }

  const members = project.members;

  return (
    <section className="overflow-hidden rounded-panel border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line-soft bg-sunken px-4 py-3">
        <span
          aria-hidden="true"
          className="size-2.5 flex-none rounded-full"
          style={{ background: project.color }}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-sm font-semibold">{project.name}</h2>
            <span className="key-badge">{project.key}</span>
            {isActive && (
              <span className="rounded-badge border border-brand-line bg-brand-tint px-1.5 py-px text-[10px] font-bold tracking-[.06em] text-brand-deep">
                ACTIVO
              </span>
            )}
          </div>
          {project.description && (
            <p className="mt-0.5 truncate text-[12.5px] text-subtle">{project.description}</p>
          )}
        </div>

        <div className="flex-1" />
        <AvatarStack users={members.map((m) => m.user)} max={5} size={24} />
        <p className="text-[12.5px] text-subtle">
          {members.length} {members.length === 1 ? 'persona' : 'personas'}
        </p>

        {!isActive && (
          <button
            type="button"
            onClick={onOpen}
            className="rounded-[7px] border border-line-strong px-[11px] py-[5px] text-[12.5px] font-semibold transition-colors hover:border-line-hover"
          >
            Abrir
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="rounded-[7px] border border-line-strong px-[11px] py-[5px] text-[12.5px] font-semibold transition-colors hover:border-line-hover"
        >
          {expanded ? 'Ocultar equipo' : 'Ver equipo'}
        </button>
      </header>

      {expanded && (
        <div className="px-4 py-3">
          <ul className="flex flex-col">
            {members.map((member) => (
              <li
                key={member.id}
                className="flex items-center gap-3 border-b border-line-soft py-2.5 last:border-b-0"
              >
                <Avatar user={member.user} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium">{member.user.fullName}</p>
                  <p className="truncate text-[11.5px] text-faint">{member.user.email}</p>
                </div>

                <div className="text-right">
                  <p className="text-[12.5px] font-semibold">{ROLE_LABEL[member.role]}</p>
                  <p className="text-[11px] text-faint">{ROLE_HINT[member.role]}</p>
                </div>

                {project.canManage && member.user.id !== project.ownerId && (
                  <button
                    type="button"
                    onClick={() => setMemberToRemove(member)}
                    className="rounded-[7px] border border-line-strong px-2 py-1 text-[11.5px] font-medium text-danger-text transition-colors hover:border-danger"
                  >
                    Sacar
                  </button>
                )}
                {member.user.id === project.ownerId && (
                  <span className="text-[11.5px] text-faint">Dueno</span>
                )}
              </li>
            ))}
          </ul>

          {project.canManage && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="mt-3 rounded-control bg-brand px-3.5 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
            >
              + Agregar personas
            </button>
          )}
        </div>
      )}

      {adding && (
        <PeoplePicker
          title={`Agregar al proyecto ${project.name}`}
          description="Vera el backlog, el tablero y el canal de chat del proyecto."
          confirmLabel="Agregar"
          excludeIds={members.map((m) => m.user.id)}
          emptyHint="Ya esta todo el equipo en este proyecto."
          pending={addMember.isPending}
          error={addMember.error ? (addMember.error as Error).message : null}
          onClose={() => setAdding(false)}
          onPick={(person) => addMember.mutate({ userId: person.id, role })}
        >
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-semibold">Rol en el proyecto</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as ProjectRole)}
              className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
            >
              {(Object.keys(ROLE_LABEL) as ProjectRole[]).map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABEL[option]} — {ROLE_HINT[option]}
                </option>
              ))}
            </select>
          </label>
        </PeoplePicker>
      )}

      {memberToRemove && (
        <ConfirmDialog
          title={`Sacar a ${memberToRemove.user.fullName}`}
          description={
            <>
              Dejara de ver el backlog, el tablero y el canal de chat de{' '}
              <strong>{project.name}</strong>, y las tareas que tenia asignadas quedaran sin
              responsable. Sus comentarios se conservan.
            </>
          }
          confirmLabel="Sacar del proyecto"
          pending={removeMember.isPending}
          error={removeMember.error ? (removeMember.error as Error).message : null}
          onCancel={() => setMemberToRemove(null)}
          onConfirm={() =>
            removeMember.mutate(memberToRemove.id, { onSuccess: () => setMemberToRemove(null) })
          }
        />
      )}
    </section>
  );
}
